import { concat, defer, Observable, of } from 'rxjs';
import { ofType } from 'redux-observable';
import { catchError, mergeMap, switchMap, withLatestFrom } from 'rxjs/operators';
import { EpicDeps } from '@wearejh/m2-pwa-engine/lib/types';
import { ApolloClient } from 'apollo-client';

import { Actions, CartAddConfigItem, CartMsg, TypeMap } from '../cart.actions';
import { cartVariantAndId } from '../utils/stateObservables';
import { addConfigurable, addConfigurableVariables } from '../queries/__generated__/addConfigurable';
import addConfigurableQuery from '../queries/addConfigurable.graphql';
import { refreshCartAndRetry } from '../utils/refreshCartAndRetry';

type InputAction = TypeMap['Cart.Add.Config'];

export function cartAddConfig(actions$: Observable<any>, state: Observable<any>, deps: EpicDeps) {
    return actions$.pipe(
        ofType<Actions, InputAction>('Cart.Add.Config'),
        withLatestFrom(cartVariantAndId(state)),
        switchMap(([action, [, cartId]]) => {
            if (!cartId) {
                console.error('Could not add since cartId was missing');
                return refreshCartAndRetry(
                    actions$,
                    action,
                    CartMsg('Cart.AddError', 'could not create cart prior to adding'),
                );
            }
            return addConfigMutation(deps, String(cartId), action.payload.items).pipe(
                mergeMap(() => {
                    return concat(of(CartMsg('Cart.AddSuccess'), CartMsg('Cart.Refresh', { force: true })));
                }),
                catchError((e) => {
                    console.error(e);
                    return of(CartMsg('Cart.AddError', e.message));
                }),
            );
        }),
    );
}

export function addConfigMutation(deps: EpicDeps, cartId: string, items: CartAddConfigItem[]) {
    return defer(() =>
        (deps.client as ApolloClient<any>).mutate<addConfigurable, addConfigurableVariables>({
            mutation: addConfigurableQuery,
            operationName: 'addConfigurable',
            variables: {
                input: {
                    cart_id: String(cartId),
                    cart_items: items,
                },
            },
            fetchPolicy: 'no-cache',
        } as any),
    );
}
